import React from 'react';
import { ArrowRight, MapPin, CalendarCheck, KeyRound, ClipboardCheck, Users } from 'lucide-react';
import { useLanguage } from '../../lib/LanguageContext';

const HERO_IMG = 'https://images.unsplash.com/photo-1670718668686-0d0cedbbfc9c?crop=entropy&cs=srgb&fm=webp&w=1600&q=80';

const SERVICE_ICONS = [CalendarCheck, KeyRound, ClipboardCheck, Users];

export const Hero = () => {
  const { t } = useLanguage();

  const handleScroll = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="top" data-testid="hero-section" className="relative pt-32 pb-14 md:pt-40 md:pb-16 overflow-hidden grain">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 right-0 w-[60%] h-[60%] bg-gradient-to-bl from-sand-100 to-transparent" />
        <div className="absolute bottom-0 left-0 w-[40%] h-[40%] bg-gradient-to-tr from-olive-300/20 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        {/* LEFT — copy */}
        <div className="lg:col-span-7 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-sand-600 mb-8">
            <span className="w-8 h-px bg-sand-500" />
            {t.hero.eyebrow}
          </div>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-[56px] text-ink-900 leading-[1.05] mb-6">
            {t.hero.headline}
          </h1>

          <p className="font-serif text-xl md:text-2xl text-sand-700 leading-snug italic mb-8 max-w-2xl">
            {t.hero.tagline}
          </p>

          <p className="text-base md:text-lg text-ink-500 leading-relaxed max-w-2xl mb-10">
            {t.hero.sub}
          </p>

          <div className="mb-10">
            <div className="text-xs uppercase tracking-[0.24em] text-sand-600 mb-5">
              {t.hero.servicesTitle}
            </div>
            <ul className="grid sm:grid-cols-2 gap-5">
              {t.hero.services.map((svc, i) => {
                const Icon = SERVICE_ICONS[i % SERVICE_ICONS.length];
                return (
                  <li
                    key={i}
                    data-testid={`hero-service-${i}`}
                    className="flex gap-4 items-start"
                  >
                    <span className="w-10 h-10 rounded-full border border-sand-300 bg-cream flex items-center justify-center shrink-0 mt-0.5">
                      <Icon className="w-4.5 h-4.5 text-sand-600" strokeWidth={1.4} />
                    </span>
                    <div>
                      <div className="text-[15px] font-medium text-ink-900 leading-snug">{svc.title}</div>
                      <div className="text-sm text-ink-500 mt-1 leading-relaxed">{svc.body}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <button
              data-testid="hero-cta-button"
              onClick={() => handleScroll('contact')}
              className="group inline-flex items-center gap-3 min-h-[52px] px-7 py-4 bg-ink-900 text-cream rounded-sm text-sm tracking-wide hover:bg-sand-600 transition-colors"
            >
              {t.hero.cta}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-secondary-button"
              onClick={() => handleScroll('services')}
              className="text-sm text-ink-900 underline underline-offset-8 decoration-sand-300 hover:decoration-sand-500 transition-colors min-h-[44px] px-1"
            >
              {t.nav.services} →
            </button>
          </div>

          <div className="mt-10 text-xs text-ink-500">
            {t.hero.meta}
          </div>
        </div>

        {/* RIGHT — image */}
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-[0_24px_60px_rgba(45,51,47,0.18)]">
            <img
              data-testid="hero-image"
              src={HERO_IMG}
              alt="Warm Spanish holiday home on the Costa Blanca"
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-900/20 via-transparent to-transparent" />
          </div>

          <div className="hidden md:flex mt-6 bg-cream border border-sand-200 px-6 py-5 rounded-sm shadow-[0_18px_40px_rgba(45,51,47,0.06)] items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-olive-500/15 text-olive-600 flex items-center justify-center shrink-0">
              <MapPin className="w-5 h-5" />
            </div>
            <div>
              <div className="font-serif text-lg leading-tight text-ink-900">Alicante · Costa Blanca</div>
              <div className="text-xs text-ink-500 mt-1 leading-relaxed">Playa de San Juan · El Campello · Mutxamel · Villajoyosa · Altea · Calpe · Moraira · Jávea</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
